import { EnvironmentProviders, ErrorHandler, importProvidersFrom, Provider } from "@angular/core";
import { DatePipe, PRECONNECT_CHECK_BLOCKLIST } from "@angular/common";
import { RouteReuseStrategy } from "@angular/router";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
    withXhr,
} from "@angular/common/http";
import { MARKED_OPTIONS, provideMarkdown } from "ngx-markdown";
import { provideNgxMask } from "ngx-mask";
import { ToastrModule } from "ngx-toastr";
import { UiSwitchModule } from "@leoborel/ngx-ui-switch";

import { HttpRequestInterceptor } from "./modules/shared/interceptors/http-request.interceptor";
import { HttpParallelizationInterceptor } from "./modules/shared/interceptors/http-parallelization.interceptor";
import { errorsInterceptorProviders } from "./modules/shared/interceptors/errors-interceptor.interceptor";
import { blobErrorsInterceptorProviders } from "./modules/shared/interceptors/blob-errors.interceptor";
import { clientIdInterceptorProviders } from "./modules/shared/interceptors/client-id.interceptor";
import { authInterceptorProviders } from "./modules/authentication/interceptors/auth-interceptor.interceptor";
import { httpCancelInterceptorProviders } from "./modules/shared/interceptors/cancelRequest.interceptor";
import { matomoInterceptorProviders } from "./modules/shared/interceptors/matomo.interceptor";
import { GlobalRedirectGuard } from "./modules/shared/guards/global-redirect-guard.service";
import { LoggedInGuardService } from "./modules/authentication/guards/logged-in-guard.service";
import { AuthGuard } from "./modules/authentication/guards/auth-guard.service";
import { WaitingGuardService } from "./modules/authentication/guards/waiting-guard.service";
import { RC_TOAST_CONFIG } from "./modules/shared/components/rc-toast/rc-toast.config";
import { MatomoErrorHandler } from "./modules/shared/services/matomo-error-handler";
import { ParamAwareRouteReuseStrategy } from "./modules/shared/routing/param-aware-route-reuse.strategy";
import { provideTooltipsMasquesAuDefilement } from "./modules/shared/utils/tooltips-defilement.util";

/**
 * Providers applicatifs globaux de la plateforme RC.
 *
 * Regroupe les providers qui etaient auparavant fournis par des NgModules
 * importes au bootstrap et les providers HTTP eparpilles dans `main.ts`.
 * Objectif : eviter que le graphe applicatif des routes lazy soit tire au bootstrap,
 * afin que le lazy-loading des routes devienne réellement effectif.
 *
 * ⚠️ ORDRE DES INTERCEPTORS HTTP : l'ordre ci-dessous reproduit EXACTEMENT l'ordre
 * d'enregistrement précédent (tableau aplati de `main.ts`) :
 *   HttpRequest -> HttpParallelization -> Errors -> BlobErrors -> ClientId -> Auth -> HttpCancel
 * Matomo est placé en tête (le plus extérieur) : il ne mesure que l'issue finale
 * d'une requête et ne la modifie pas.
 * Ne pas réordonner sans vérifier l'impact sur l'auth (token/401) et la gestion d'erreurs.
 */
export const RC_PLATFORM_APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
    // --- Animations Angular : anciennement fournies par BrowserAnimationsModule
    //     importé dans SharedModule au bootstrap. Indispensable pour les propriétés
    //     synthétiques (@sidebarNotificationText, @slideToggle, etc.), sinon NG05105. ---
    provideAnimations(),

    // --- HttpClient : XHR is required for upload/download progress events. ---
    provideHttpClient(withInterceptorsFromDi(), withXhr()),

    // --- Interceptors HTTP (ordre identique à l'ancien bootstrap, voir note ci-dessus) ---
    matomoInterceptorProviders,
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpParallelizationInterceptor, multi: true },
    errorsInterceptorProviders,
    blobErrorsInterceptorProviders,
    clientIdInterceptorProviders,
    authInterceptorProviders,
    httpCancelInterceptorProviders,

    // --- NgOptimizedImage : les images `priority` (logo de l'écran de chargement)
    //     sont servies par l'application elle-même. Un <link rel="preconnect"> vers
    //     sa propre origine n'apporte rien, on neutralise donc l'avertissement
    //     NG02956 (dev-only) pour l'origine courante. ---
    { provide: PRECONNECT_CHECK_BLOCKLIST, useValue: window.location.origin },

    // --- Providers applicatifs globaux ---
    // Erreurs Angular remontées à Matomo (Zone.js les soustrait à `window.onerror`).
    { provide: ErrorHandler, useClass: MatomoErrorHandler },
    provideNgxMask(),
    DatePipe,
    GlobalRedirectGuard,
    // Changer d'`:id` (lien d'une fiche vers une autre) recrée la page au lieu de
    // garder l'instance de la fiche précédente — voir la stratégie.
    { provide: RouteReuseStrategy, useClass: ParamAwareRouteReuseStrategy },
    // Une infobulle ne survit pas au défilement d'une zone (voir l'utilitaire).
    provideTooltipsMasquesAuDefilement(),

    // --- Guards d'authentification (anciennement fournis au bootstrap via AuthenticationModule).
    //     LoggedInGuardService et WaitingGuardService ne sont PAS providedIn:'root' :
    //     ils doivent rester explicitement fournis ici, sinon la route /auth casse. ---
    LoggedInGuardService,
    AuthGuard,
    WaitingGuardService,

    // --- Modules à configuration racine (forRoot) : Toastr/UiSwitch ---
    importProvidersFrom(
        ToastrModule.forRoot(RC_TOAST_CONFIG),
        UiSwitchModule.forRoot({}),
    ),

    // --- Markdown : configuration racine unique ---
    provideMarkdown({
        loader: HttpClient,
        markedOptions: {
            provide: MARKED_OPTIONS,
            useValue: {
                gfm: true,
                breaks: false,
                pedantic: false,
            },
        },
    }),
];
